import React, { useState, useContext } from "react";
import { useNotifications } from "@mantine/notifications";
import { GlobalContext } from "../utils/GlobalContext";
import { auth, db } from "../utils/firebaseConfig";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Button,
  Drawer,
  Popover,
  TextInput,
  PasswordInput,
  Center,
} from "@mantine/core";
import { MdOutlineEmail, MdPassword, MdLogin, MdLogout } from "react-icons/md";
import "../styles/components/Navbar.css";
import SignupDrawer from "./SignupDrawer";

const Navbar = ({ isDrawerOpen, setIsDrawerOpen }) => {
  // Hooks
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const { setUser, isLoggedIn, setIsLoggedIn, userInfo, setUserInfo } =
    useContext(GlobalContext);

  const notifications = useNotifications();

  const navigate = useNavigate();

  // Function to handle login
  const handleLogin = async () => {
    let errorMessage = "";
    let tempUserInfo = "";

    if (email === "" || password === "") {
      // setErrorMessage("Please fill in all fields.");
      notifications.showNotification({
        title: "ERROR",
        message: "Please fill in all fields.",
        color: "red",
        disallowClose: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Login user
      const user = await signInWithEmailAndPassword(auth, email, password);

      // Populate user info in global userInfo state
      const q = query(
        collection(db, "users"),
        where("email", "==", user.user.email)
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        // doc.data() is never undefined for query doc snapshots
        setUserInfo(doc.data());
        tempUserInfo = doc.data();
      });

      setUser(user);
      setIsLoggedIn(true);
      setLoading(false);
      setIsPopoverOpen(false);

      // Navigate to appropriate page based on user type
      tempUserInfo.userType === "patient"
        ? navigate("/patientdashboard")
        : navigate("/doctordashboard");
    } catch (error) {
      // Handle errors
      if (error.message.includes("user-not-found")) {
        errorMessage = "User not found.";
      } else if (error.message.includes("invalid-email")) {
        errorMessage = "Please enter a valid email.";
      } else if (error.message.includes("wrong-password")) {
        errorMessage = "Wrong password.";
      } else {
        errorMessage = "An error occurred. Try again.";
      }

      // Show error notification
      notifications.showNotification({
        title: "ERROR",
        message: errorMessage,
        color: "red",
        disallowClose: true,
      });

      setLoading(false);
    }
  };

  // Function to handle logout
  const handleLogout = async () => {
    try {
      signOut(auth);
      setUser(null);
      setIsLoggedIn(false);
      navigate("/");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="Navbar">
      <div className="navbarMenu">
        {!isLoggedIn && (
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive ? "activeNavbarMenuItem" : "navbarMenuItem"
            }
          >
            Home
          </NavLink>
        )}

        {/* Patient dashboard */}
        {isLoggedIn && userInfo.userType === "patient" && (
          <NavLink
            to="/patientdashboard"
            className={({ isActive }) =>
              isActive ? "activeNavbarMenuItem" : "navbarMenuItem"
            }
          >
            Patient Dashboard
          </NavLink>
        )}

        {/* Patient profile */}
        {isLoggedIn && userInfo.userType === "patient" && (
          <NavLink
            to="/patientprofile"
            className={({ isActive }) =>
              isActive ? "activeNavbarMenuItem" : "navbarMenuItem"
            }
          >
            Patient Profile
          </NavLink>
        )}

        {/* Doctor profile */}
        {isLoggedIn && userInfo.userType === "doctor" && (
          <NavLink
            to="/doctordashboard"
            className={({ isActive }) =>
              isActive ? "activeNavbarMenuItem" : "navbarMenuItem"
            }
          >
            Doctor Dashboard
          </NavLink>
        )}

        {/* Doctor profile */}
        {isLoggedIn && userInfo.userType === "doctor" && (
          <NavLink
            to="/doctorprofile"
            className={({ isActive }) =>
              isActive ? "activeNavbarMenuItem" : "navbarMenuItem"
            }
          >
            Doctor Profile
          </NavLink>
        )}

        {/* EHR */}
        {isLoggedIn && userInfo.userType === "doctor" && (
          <NavLink
            to="/ehr"
            className={({ isActive }) =>
              isActive ? "activeNavbarMenuItem" : "navbarMenuItem"
            }
          >
            EHR
          </NavLink>
        )}
      </div>
      <div className="navAuth">
        {/* Login Popover */}
        {!isLoggedIn && (
          <Popover
            opened={isPopoverOpen}
            onClose={() => setIsPopoverOpen(false)}
            title="Login"
            withCloseButton
            width={260}
            gutter={5}
            withArrow
            position="bottom"
            placement="center"
            radius="md"
            shadow="lg"
            target={
              <Button
                onClick={() => setIsPopoverOpen((prev) => !prev)}
                variant="light"
                color="indigo"
                size="md"
                style={{ marginRight: 10 }}
              >
                Login
              </Button>
            }
          >
            <div>
              <TextInput
                icon={<MdOutlineEmail />}
                placeholder="Email"
                label="Email"
                required
                style={{ marginBottom: 10 }}
                onChange={(e) => setEmail(e.target.value)}
              />
              <PasswordInput
                icon={<MdPassword />}
                placeholder="Password"
                label="Password"
                required
                style={{ marginBottom: 15 }}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Center>
                <Button
                  leftIcon={<MdLogin />}
                  color="indigo"
                  onClick={handleLogin}
                  loading={loading}
                >
                  Login
                </Button>
              </Center>
            </div>
          </Popover>
        )}

        {!isLoggedIn && (
          <Button
            variant="filled"
            color="indigo"
            size="md"
            onClick={() => setIsDrawerOpen(true)}
          >
            Sign Up
          </Button>
        )}

        {isLoggedIn && (
          <Button
            leftIcon={<MdLogout />}
            variant="filled"
            color="red"
            size="md"
            onClick={handleLogout}
          >
            Logout
          </Button>
        )}
      </div>

      <Drawer
        opened={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        position="right"
        title="Sign Up"
        padding="xl"
        size="xl"
      >
        <SignupDrawer
          setIsDrawerOpen={setIsDrawerOpen}
          setIsPopoverOpen={setIsPopoverOpen}
        />
      </Drawer>
    </div>
  );
};

export default Navbar;
